import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { accesApi, type Utilisateur } from '../../services/acces-api';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Champ,
  Etiquette,
  Saisie,
  Section,
  Selection,
  ValeurCalculee,
} from '../../components/ndf/ui/Champ';
import { erreurGlobale, erreursParChamp, type ErreursChamps } from '../../components/ndf/ui/erreurs-champs';
import { MessageErreur } from '../../components/ndf/ui/MessageEtat';
import { useToast } from '../../context/ToastContext';

/**
 * Utilisateurs — la SEULE porte pour ouvrir un accès à Gertrude.
 *
 * Un utilisateur porte son accès : statut du compte, profil (ce qu'il peut
 * faire), rôle (ce qu'il voit), rattachement hiérarchique, et un lien
 * facultatif vers sa fiche salarié. La fiche, elle, ne décrit que la vie du
 * salarié dans l'entreprise.
 *
 * Règle d'écran de l'application : on ouvre en LECTURE, on modifie sur un
 * geste explicite, l'enregistrement y met fin.
 */

const STATUTS: Record<
  Utilisateur['statut_compte'],
  { libelle: string; ton: 'neutre' | 'actif' | 'positif' | 'alerte' | 'attention'; aide: string }
> = {
  cree: {
    libelle: 'Créé',
    ton: 'neutre',
    aide: 'Le compte existe mais l’accès n’est pas ouvert : cette personne ne peut pas se connecter.',
  },
  invite: {
    libelle: 'Invité',
    ton: 'attention',
    aide: 'L’accès est ouvert. Le statut passera à « Actif » à la première connexion.',
  },
  actif: { libelle: 'Actif', ton: 'positif', aide: 'Cette personne utilise l’application.' },
  suspendu: {
    libelle: 'Suspendu',
    ton: 'attention',
    aide: 'Accès coupé temporairement, sur toute l’application.',
  },
  desactive: {
    libelle: 'Désactivé',
    ton: 'alerte',
    aide: 'Accès fermé. Les données restent conservées.',
  },
};

const ACTIONS: Record<
  Utilisateur['statut_compte'],
  Array<{ vers: Utilisateur['statut_compte']; libelle: string; principal?: boolean }>
> = {
  cree: [{ vers: 'invite', libelle: 'Ouvrir l’accès', principal: true }],
  invite: [
    { vers: 'suspendu', libelle: 'Suspendre' },
    { vers: 'desactive', libelle: 'Fermer l’accès' },
  ],
  actif: [
    { vers: 'suspendu', libelle: 'Suspendre' },
    { vers: 'desactive', libelle: 'Fermer l’accès' },
  ],
  suspendu: [
    { vers: 'actif', libelle: 'Rétablir l’accès', principal: true },
    { vers: 'desactive', libelle: 'Fermer l’accès' },
  ],
  desactive: [{ vers: 'invite', libelle: 'Rouvrir l’accès', principal: true }],
};

function dateCourte(v: string | null) {
  if (!v) return null;
  return new Date(v).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

const CHAMPS_MODIFIABLES = [
  'name',
  'email',
  'profil_id',
  'role_id',
  'manager_id',
  'salarie_id',
  'role',
] as const;

function corpsModifiable(f: Partial<Utilisateur>): Partial<Utilisateur> {
  const body: Record<string, unknown> = {};
  for (const cle of CHAMPS_MODIFIABLES) if (cle in f) body[cle] = f[cle];
  return body as Partial<Utilisateur>;
}

export function UtilisateursPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [selection, setSelection] = useState<string | null>(null);
  const [creation, setCreation] = useState(false);
  const [edition, setEdition] = useState(false);
  const [erreurs, setErreurs] = useState<ErreursChamps>({});

  const { data, isLoading, error } = useQuery({
    queryKey: ['acces', 'utilisateurs'],
    queryFn: () => accesApi.utilisateurs.liste(),
    retry: false,
  });
  const { data: profilsData } = useQuery({ queryKey: ['acces', 'profils'], queryFn: () => accesApi.profils.liste() });
  const { data: rolesData } = useQuery({ queryKey: ['acces', 'roles'], queryFn: () => accesApi.roles.liste() });
  const { data: fichesData } = useQuery({
    queryKey: ['acces', 'fiches'],
    queryFn: () => accesApi.fichesSalaries(),
  });

  const utilisateurs = data?.utilisateurs ?? [];
  const courant = useMemo(
    () => utilisateurs.find((u) => u.id === selection) ?? null,
    [utilisateurs, selection],
  );

  const invalider = () => qc.invalidateQueries({ queryKey: ['acces'] });

  const enregistrer = useMutation({
    mutationFn: (body: Partial<Utilisateur>) =>
      selection ? accesApi.utilisateurs.modifier(selection, body) : accesApi.utilisateurs.creer(body),
    onSuccess: (res) => {
      setErreurs({});
      toast.success(`Compte de ${res.utilisateur.name ?? res.utilisateur.email} enregistré.`);
      setCreation(false);
      setEdition(false);
      setSelection(res.utilisateur.id);
      invalider();
    },
    onError: (err) => {
      setErreurs(erreursParChamp(err));
      const global = erreurGlobale(err);
      if (global) toast.error(global);
    },
  });

  const changerStatut = useMutation({
    mutationFn: ({ id, statut }: { id: string; statut: Utilisateur['statut_compte'] }) =>
      accesApi.utilisateurs.changerStatut(id, statut),
    onSuccess: (res) => {
      const nom = res.utilisateur.name ?? res.utilisateur.email;
      toast.success(
        res.utilisateur.peut_se_connecter
          ? `${nom} peut maintenant se connecter.`
          : `L’accès de ${nom} est fermé.`,
      );
      invalider();
    },
    onError: (err) => toast.error(erreurGlobale(err) ?? 'Le changement de statut a échoué.'),
  });

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-titre text-xl font-bold text-nuit">Utilisateurs</h1>
          <p className="text-sm text-nuit/70">
            Qui accède à Gertrude, avec quel profil et sur quel périmètre.
          </p>
        </div>
        <BoutonPrincipal
          type="button"
          onClick={() => {
            setSelection(null);
            setCreation(true);
            setEdition(true);
            setErreurs({});
          }}
        >
          Nouvel utilisateur
        </BoutonPrincipal>
      </header>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,22rem)_1fr]">
        <div className="overflow-hidden rounded-lg border border-nuit/10 bg-white">
          {isLoading && <p className="p-4 text-sm text-nuit/60">Chargement…</p>}
          {error && (
            <div className="p-3">
              <MessageErreur erreur={error} />
            </div>
          )}
          <ul className="divide-y divide-nuit/5">
            {utilisateurs.map((u) => (
              <li key={u.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelection(u.id);
                    setCreation(false);
                    setEdition(false);
                    setErreurs({});
                  }}
                  className={`w-full px-4 py-3 text-left hover:bg-ciel/20 ${
                    selection === u.id ? 'bg-ciel/30' : ''
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-nuit">{u.name ?? u.email}</span>
                    <Etiquette ton={STATUTS[u.statut_compte]?.ton ?? 'neutre'}>
                      {STATUTS[u.statut_compte]?.libelle ?? u.statut_compte}
                    </Etiquette>
                  </span>
                  <span className="mt-0.5 block text-xs text-nuit/60">{u.email}</span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    {u.profil_nom && <Etiquette ton="actif">{u.profil_nom}</Etiquette>}
                    {u.role_nom && <Etiquette>{u.role_nom}</Etiquette>}
                    {u.role === 'admin' && <Etiquette ton="attention">Admin Gertrude</Etiquette>}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          {(courant || creation) && (
            <FormulaireUtilisateur
              key={`${courant?.id ?? 'nouveau'}-${edition ? 'modif' : 'lecture'}`}
              utilisateur={courant}
              utilisateurs={utilisateurs}
              profils={profilsData?.profils ?? []}
              roles={rolesData?.roles ?? []}
              fiches={fichesData?.fiches ?? []}
              edition={edition || creation}
              creation={creation}
              enCours={enregistrer.isPending}
              statutEnCours={changerStatut.isPending}
              erreurs={erreurs}
              onModifier={() => {
                setEdition(true);
                setErreurs({});
              }}
              onAnnuler={() => {
                setEdition(false);
                setCreation(false);
                setErreurs({});
              }}
              onEnregistrer={(body) => enregistrer.mutate(body)}
              onChangerStatut={(statut) =>
                courant && changerStatut.mutate({ id: courant.id, statut })
              }
            />
          )}
          {!courant && !creation && (
            <p className="rounded-lg border border-dashed border-nuit/20 p-8 text-center text-sm text-nuit/60">
              Sélectionnez un utilisateur à gauche pour le consulter.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function FormulaireUtilisateur({
  utilisateur,
  utilisateurs,
  profils,
  roles,
  fiches,
  edition,
  creation,
  enCours,
  statutEnCours,
  erreurs,
  onModifier,
  onAnnuler,
  onEnregistrer,
  onChangerStatut,
}: {
  utilisateur: Utilisateur | null;
  utilisateurs: Utilisateur[];
  profils: Array<{ id: string; nom: string; actif: boolean }>;
  roles: Array<{ id: string; nom: string; actif: boolean }>;
  fiches: Array<{ id: string; nom: string; rattachee_a: string | null }>;
  edition: boolean;
  creation: boolean;
  enCours: boolean;
  statutEnCours: boolean;
  erreurs: ErreursChamps;
  onModifier: () => void;
  onAnnuler: () => void;
  onEnregistrer: (body: Partial<Utilisateur>) => void;
  onChangerStatut: (statut: Utilisateur['statut_compte']) => void;
}) {
  const [f, setF] = useState<Partial<Utilisateur>>(
    utilisateur ?? { email: '', name: '', role: 'user' },
  );
  const lecture = !edition;
  const maj = <K extends keyof Utilisateur>(cle: K, v: Utilisateur[K]) =>
    setF((p) => ({ ...p, [cle]: v }));

  const statut = utilisateur?.statut_compte ?? 'cree';
  const meta = STATUTS[statut];
  const nomProfil = profils.find((p) => p.id === f.profil_id)?.nom ?? null;
  const nomRole = roles.find((r) => r.id === f.role_id)?.nom ?? null;
  const nomManager = utilisateurs.find((u) => u.id === f.manager_id);
  const ficheLiee = fiches.find((x) => x.id === f.salarie_id);

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        onEnregistrer(corpsModifiable(f));
      }}
    >
      {utilisateur && (
        <Section titre="Accès" teinte="violet">
          <div className="flex flex-wrap items-center gap-2">
            <Etiquette ton={meta.ton}>{meta.libelle}</Etiquette>
            <span className="text-sm text-nuit/70">{meta.aide}</span>
          </div>
          <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
            <div className="flex gap-2">
              <dt className="text-nuit/60">Connexion&nbsp;:</dt>
              <dd className="text-nuit">compte Google {utilisateur.email}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-nuit/60">Dernière connexion&nbsp;:</dt>
              <dd className="text-nuit">{dateCourte(utilisateur.derniere_connexion_at) ?? 'jamais'}</dd>
            </div>
          </dl>
          <div className="flex flex-wrap gap-2 border-t border-nuit/10 pt-3">
            {(ACTIONS[statut] ?? []).map((a) =>
              a.principal ? (
                <BoutonPrincipal
                  key={a.vers}
                  type="button"
                  disabled={statutEnCours}
                  onClick={() => onChangerStatut(a.vers)}
                >
                  {statutEnCours ? 'En cours…' : a.libelle}
                </BoutonPrincipal>
              ) : (
                <BoutonSecondaire
                  key={a.vers}
                  type="button"
                  disabled={statutEnCours}
                  onClick={() => onChangerStatut(a.vers)}
                >
                  {a.libelle}
                </BoutonSecondaire>
              ),
            )}
          </div>
        </Section>
      )}

      <Section titre="Identité" repliable>
        <div className="grid gap-4 sm:grid-cols-2">
          <Champ label="Nom affiché" erreur={erreurs.name}>
            {lecture ? (
              <ValeurCalculee>{f.name ?? ''}</ValeurCalculee>
            ) : (
              <Saisie value={f.name ?? ''} onChange={(e) => maj('name', e.target.value)} />
            )}
          </Champ>
          <Champ label="E-mail" requis erreur={erreurs.email} aide="L’adresse Google de connexion.">
            {lecture ? (
              <ValeurCalculee>{f.email ?? ''}</ValeurCalculee>
            ) : (
              <Saisie
                type="email"
                required
                value={f.email ?? ''}
                onChange={(e) => maj('email', e.target.value)}
              />
            )}
          </Champ>
        </div>
      </Section>

      <Section
        titre="Droits"
        teinte="vert"
        description="Le profil dit ce que la personne peut faire, le rôle sur quels enregistrements."
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Champ label="Profil" erreur={erreurs.profil_id}>
            {lecture ? (
              <ValeurCalculee>{nomProfil ?? ''}</ValeurCalculee>
            ) : (
              <Selection value={f.profil_id ?? ''} onChange={(e) => maj('profil_id', e.target.value || null)}>
                <option value="">Aucun</option>
                {profils.filter((p) => p.actif).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nom}
                  </option>
                ))}
              </Selection>
            )}
          </Champ>
          <Champ label="Rôle" erreur={erreurs.role_id}>
            {lecture ? (
              <ValeurCalculee>{nomRole ?? ''}</ValeurCalculee>
            ) : (
              <Selection value={f.role_id ?? ''} onChange={(e) => maj('role_id', e.target.value || null)}>
                <option value="">Aucun</option>
                {roles.filter((r) => r.actif).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nom}
                  </option>
                ))}
              </Selection>
            )}
          </Champ>
          <Champ
            label="Manager"
            erreur={erreurs.manager_id}
            aide="Sert à approuver et à définir le périmètre « Mon équipe »."
          >
            {lecture ? (
              <ValeurCalculee>{nomManager?.name ?? nomManager?.email ?? ''}</ValeurCalculee>
            ) : (
              <Selection value={f.manager_id ?? ''} onChange={(e) => maj('manager_id', e.target.value || null)}>
                <option value="">Aucun</option>
                {utilisateurs
                  .filter((u) => u.id !== utilisateur?.id)
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name ?? u.email}
                    </option>
                  ))}
              </Selection>
            )}
          </Champ>
          <Champ
            label="Droit administrateur global"
            aide="Commande les modules pas encore migrés : factures, paiements, auto-facturation, support."
          >
            {lecture ? (
              <ValeurCalculee>{f.role === 'admin' ? 'Oui' : 'Non'}</ValeurCalculee>
            ) : (
              <Selection
                value={f.role ?? 'user'}
                onChange={(e) => maj('role', e.target.value as 'admin' | 'user')}
              >
                <option value="user">Non</option>
                <option value="admin">Oui</option>
              </Selection>
            )}
          </Champ>
        </div>
      </Section>

      <Section
        titre="Fiche salarié"
        teinte="jaune"
        repliable
        description="Rattachement facultatif : un utilisateur n’est pas forcément salarié de KESTIO."
      >
        <Champ label="Fiche rattachée" erreur={erreurs.salarie_id}>
          {lecture ? (
            <ValeurCalculee>{ficheLiee?.nom ?? ''}</ValeurCalculee>
          ) : (
            <Selection value={f.salarie_id ?? ''} onChange={(e) => maj('salarie_id', e.target.value || null)}>
              <option value="">Aucune</option>
              {fiches
                // Une fiche déjà portée par quelqu'un d'autre n'est pas
                // proposée : deux comptes ne peuvent pas revendiquer la même
                // personne.
                .filter((x) => !x.rattachee_a || x.id === f.salarie_id)
                .map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.nom}
                  </option>
                ))}
            </Selection>
          )}
        </Champ>
      </Section>

      <div className="sticky bottom-0 flex justify-end gap-2 border-t border-nuit/10 bg-white/95 py-3 backdrop-blur">
        {lecture ? (
          <BoutonPrincipal type="button" onClick={onModifier}>
            Modifier
          </BoutonPrincipal>
        ) : (
          <>
            <BoutonSecondaire type="button" disabled={enCours} onClick={onAnnuler}>
              Annuler
            </BoutonSecondaire>
            <BoutonPrincipal type="submit" disabled={enCours}>
              {enCours ? 'Enregistrement…' : creation ? 'Créer le compte' : 'Enregistrer'}
            </BoutonPrincipal>
          </>
        )}
      </div>
    </form>
  );
}
